import { Box, DataTable, Text } from "grommet";
import { Dictionary, flatten, get, groupBy, mapValues, orderBy, uniqBy, values } from "lodash";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import LiveHub from "../general/LiveHub";
import { StatIcon } from "../stats/StatIcon";
import { StatValue } from "../stats/StatRenderer";
import { TeamName } from "../teams/TeamName";
import { getSeedMatch, ParticipantStatSummary, SeedMatchStatViewModel, SeedMatchViewModel } from "./SeedMatchesStore";

export const SeedMatchStatTableWrapper = () => {
    let { id } = useParams<{ id: string }>();

    const [seedMatch, setSeedMatch] = React.useState(new SeedMatchViewModel());
    const [participantRoundStats, setParticipantRoundStats] = React.useState([] as ParticipantStatSummary[]);
    const [rounds, setRounds] = React.useState([] as string[]);

    React.useEffect(() => {
        resolve();
    }, []);

    const resolve = () => {
        getSeedMatch(id).then((seedMatch) => {
            setSeedMatch(seedMatch);

            let seedMatchStatsToSeedMatchSummary = (stats: Dictionary<SeedMatchStatViewModel[]>) => {
                return mapValues(stats, (s) => s.reduce((a, b) => ({ ...b, value: a.value + b.value }), { value: 0 } as SeedMatchStatViewModel));
            };

            let stats = seedMatch.participants.map(
                (p) =>
                    ({
                        id: p.id,
                        team: p.team,
                        stats: groupBy(p.stats, (s) => "Round " + s.round),
                        Total: seedMatchStatsToSeedMatchSummary(groupBy(p.stats, (p) => p.statID)),
                    } as ParticipantStatSummary)
            );

            let presentStatTypes = uniqBy(flatten(seedMatch.participants.map((p) => p.stats.map((s) => s.stat))), (s) => s.id);

            setParticipantRoundStats(
                orderBy(
                    stats,
                    presentStatTypes.map((s) => (p) => {
                        if (get(p, `Total.${s.id}.value`, 0) != 0) {
                            return get(p, `Total.${s.id}.value`);
                        } else {
                            return s.invertedScoring ? Number.MAX_VALUE : Number.MIN_VALUE;
                        }
                    }),
                    presentStatTypes.map((s) => (s.invertedScoring ? "asc" : "desc"))
                )
            );

            let _rounds = new Array(seedMatch.rounds).fill("Round ").map((x, i) => x + (i + 1));
            setRounds(_rounds);
        });
    };

    let navigate = useNavigate();

    return (
        <LiveHub entities={[id]} handleUpdate={resolve}>
            <SeedMatchStatTable stats={participantRoundStats} rounds={rounds}></SeedMatchStatTable>
        </LiveHub>
    );
};

const SeedMatchStatTable = ({ stats, rounds }) => {
    const [statRoundColumns, setStatRoundColumns] = React.useState([] as any);

    React.useEffect(() => {
        setStatRoundColumns([
            ...rounds.map((round, i) => ({
                property: `round${i + 1}`,
                header: <Text style={{ width: "80px" }}>{round}</Text>,
                render: (participant) => (
                    <>
                        {participant.stats[round] &&
                            participant.stats[round].map(({ stat, value }) => (
                                <Text key={participant.id + round + stat.id}>
                                    {StatIcon(stat)} {StatValue(stat, value)}
                                </Text>
                            ))}
                    </>
                ),
            })),
            {
                property: `Total`,
                header: "Total",
                render: (participant) => (
                    <>
                        {participant["Total"] &&
                            values(participant["Total"]).map(({ stat, value }) => (
                                <Text key={participant.id + "Total" + stat.id}>
                                    {StatIcon(stat)} {StatValue(stat, value)}
                                </Text>
                            ))}
                    </>
                ),
            },
        ]);
    }, [stats,rounds]);

    return (
        <>
            <DataTable
                data={stats}
                fill
                columns={[
                    {
                        property: "id",
                        primary: true,
                        header: "#",
                        render: (participant) => (
                            <Text size="large" weight="bold">
                                {stats.indexOf(participant) + 1}
                            </Text>
                        ),
                    },
                    {
                        property: "team.name",
                        header: "Team",
                        render: (participant) => (
                            <Box direction="row" align="center" gap="small">
                                <img src={participant.team.logoUri} width="60px" alt="" />
                                <TeamName team={participant.team} size="large" color={participant.team.color}></TeamName>
                            </Box>
                        ),
                    },
                    ...statRoundColumns,
                ]}
            ></DataTable>
        </>
    );
};

export default SeedMatchStatTable;
